import React, { Fragment, useEffect } from "react";

import clsx from "clsx";
import _, { pick } from "lodash";
import { Link } from "react-router-dom";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";

import { useUser } from "@components/user-context";
import { Blanket } from "@components/utilities";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  type Breakdown,
  type BreakdownConfig,
  type CategoricalBreakdownConfig,
} from "@north-beam/nb-common";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { Modal } from "@shared/modal";
import { Select } from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";
import { TextInput } from "@shared/text-input";
import { arrayMove } from "@utils/index";

export interface BreakdownSelectorProps {
  label?: string | React.ReactElement;
  className?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  current: Breakdown[];
  available: BreakdownConfig[];
  allConfigs: BreakdownConfig[];
  onUpdate: (breakdowns: Breakdown[]) => void;
  includeOtherCheckbox?: boolean;
  maxBreakdowns?: number;
  placeholder?: string;
  tabIndex?: string;
  menuClassName?: string;
  editBreakdownButton?: boolean;
  buttonClassName?: string;
}

export function BreakdownSelector({
  current: unfilteredCurrent,
  available,
  onUpdate,
  isDisabled = false,
  isLoading = false,
  includeOtherCheckbox,
  maxBreakdowns,
  allConfigs,
  placeholder,
  tabIndex,
  menuClassName,
}: BreakdownSelectorProps) {
  const current = React.useMemo(() => {
    const rv: Breakdown[] = [];
    for (const breakdown of unfilteredCurrent) {
      const config = allConfigs.find((c) => c.key === breakdown.key);
      if (!config) {
        continue;
      }
      let remainingValues: string[] = breakdown.values;

      if (config.type === "categorical") {
        remainingValues = [];
        for (const value of breakdown.values) {
          if (config.choices.some(({ value: v }: any) => value === v)) {
            remainingValues.push(value);
          }
        }
      }

      rv.push({ ...breakdown, values: remainingValues });
    }
    return rv;
  }, [allConfigs, unfilteredCurrent]);

  const stillAvailable = React.useMemo(() => {
    const keys = [...current.map((v) => v.key)];
    return available.filter((bdo) => !keys.includes(bdo.key));
  }, [current, available]);

  const onBreakdownUpdate = React.useCallback(
    (key: string, update: BreakdownUpdate | null) => {
      const rv = [...current];
      const bdIndex = current.findIndex((bd) => bd.key === key);
      if (bdIndex >= 0) {
        if (update === null) {
          rv.splice(bdIndex, 1);
        } else {
          rv[bdIndex] = { ...rv[bdIndex], ...update };
        }

        onUpdate(rv);
      }
    },
    [onUpdate, current],
  );

  const addBreakdown = React.useCallback(
    (config: any) => {
      if (!config) {
        return;
      }
      onUpdate([...current, newBreakdownFromConfig(config)]);
    },
    [onUpdate, current],
  );

  const showAddBreakdownDropdown =
    stillAvailable.length > 0 &&
    (typeof maxBreakdowns === "undefined"
      ? true
      : current.length <= maxBreakdowns);

  return (
    <>
      <BreakdownList
        useDragHandle={true}
        distance={10}
        helperClass="z-20 cursor-grab"
        breakdowns={current}
        breakdownDisabled={isLoading || isDisabled}
        allConfigs={allConfigs}
        onBreakdownUpdate={onBreakdownUpdate}
        includeOtherCheckbox={includeOtherCheckbox || false}
        onSortEnd={({ oldIndex, newIndex }) =>
          onUpdate(arrayMove(current, oldIndex, newIndex))
        }
      />
      {showAddBreakdownDropdown && (
        <Select
          isLoading={isLoading}
          onChange={addBreakdown}
          value={null}
          options={stillAvailable.map((breakdown) => ({
            value: breakdown,
            label: breakdown.name,
          }))}
          icon={<i className="fa-regular fa-sharp fa-chart-pie" />}
          placeholder={placeholder || "Add breakdown..."}
          tabIndex={tabIndex ? Number(tabIndex) : undefined}
          menuClassName={menuClassName}
        />
      )}
    </>
  );
}

type BreakdownUpdate = Omit<Breakdown, "key">;

interface BreakdownBoxProps {
  breakdownIndex: number;
  current: BreakdownUpdate;
  breakdownDisabled: boolean;
  config: BreakdownConfig;
  onUpdate: (key: string, changes: BreakdownUpdate | null) => void;
  includeOtherCheckbox: boolean;
}

const BreakdownBoxHandle = SortableHandle(() => <span className="grippy" />);

const BreakdownBox = SortableElement<BreakdownBoxProps>(
  ({
    current,
    config,
    onUpdate: _onUpdate,
    breakdownDisabled: disabled,
    breakdownIndex: index,
    includeOtherCheckbox,
  }: BreakdownBoxProps) => {
    const { values, groupUnselectedIntoOther } = current;
    const { key, choices: availableChoices } =
      config as CategoricalBreakdownConfig;
    const [isOpen, setOpen] = React.useState(false);
    const numValues = values.length;

    const onUpdate = React.useCallback(
      (vs: string[]) => {
        _onUpdate(key, {
          ...current,
          values: vs,
        });
        setOpen(false);
      },
      [_onUpdate, current, key],
    );

    return (
      <div
        className={clsx("mb-3 pr-2 py-2 pl-0 rounded-md", {
          "bg-white": !disabled,
          "bg-light": disabled,
        })}
      >
        <button
          type="button"
          className="close"
          aria-label="Close"
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              _onUpdate(config.key, null);
            }
          }}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <BreakdownBoxHandle />
        <div className="flex flex-column" style={{ minWidth: 0 }}>
          <p className="small font-bold my-1">
            {index + 1}. {config.name}
          </p>
          {config.type === "categorical" ? (
            <>
              <Modal
                size="md"
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                hideCloseButton
              >
                <MultiSelectControlPopup
                  labelKey={key}
                  disabled={disabled}
                  availableChoices={availableChoices}
                  values={values}
                  onUpdate={onUpdate}
                  closeDialog={() => setOpen(false)}
                />
              </Modal>
              <button
                type="button"
                className="alert alert-primary mb-2 flex text-nowrap small cursor-pointer px-2 py-1"
                onClick={() => setOpen(true)}
              >
                {numValues} values (click to edit)
              </button>
              {includeOtherCheckbox && (
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`other_${key}`}
                    checked={groupUnselectedIntoOther}
                    onChange={() =>
                      _onUpdate(key, {
                        ...current,
                        groupUnselectedIntoOther: !groupUnselectedIntoOther,
                      })
                    }
                  />
                  <label htmlFor={`other_${key}`}>
                    Group unselected values into &ldquo;Other&rdquo; category
                  </label>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    );
  },
);

interface MultiSelectControlProps {
  labelKey: string;
  disabled: boolean;
  availableChoices: { label: string; value: string }[];
  values: string[];
  onUpdate: (newValue: string[]) => void;
  closeDialog: () => void;
}

function MultiSelectControlPopup({
  labelKey,
  disabled,
  availableChoices,
  values,
  onUpdate,
  closeDialog,
}: MultiSelectControlProps) {
  const initialState = React.useMemo(
    () =>
      _.chain(values)
        .map((v) => [v, true])
        .fromPairs()
        .value(),
    [values],
  );
  const [currentState, setCurrentState] = React.useState(initialState);
  const numSelected = React.useMemo(
    () => Object.keys(currentState).length,
    [currentState],
  );
  const canSave = numSelected > 0;
  const [filterText, setFilterText] = React.useState("");
  const ftLower = filterText.toLowerCase();

  const filteredChoices = React.useMemo(
    () =>
      availableChoices.filter(
        (v) =>
          v.label.toLowerCase().indexOf(ftLower) >= 0 ||
          v.value.toLowerCase().indexOf(ftLower) >= 0,
      ),
    [availableChoices, ftLower],
  );
  const allChoices = React.useMemo(
    () =>
      _.chain(filteredChoices)
        .map((v) => [v.value, true])
        .fromPairs()
        .value(),
    [filteredChoices],
  );

  const toggle = React.useCallback(
    (value: string) => {
      setCurrentState((state) => {
        const newState = { ...state };
        if (state[value]) {
          delete newState[value];
        } else {
          newState[value] = true;
        }
        return newState;
      });
    },
    [setCurrentState],
  );

  const selectAll = React.useCallback(
    () => setCurrentState(allChoices),
    [allChoices, setCurrentState],
  );

  const clear = React.useCallback(
    () =>
      setCurrentState((state) => {
        const newState = { ...state };
        for (const value in allChoices) {
          if (newState[value]) {
            delete newState[value];
          }
        }
        return newState;
      }),
    [allChoices, setCurrentState],
  );

  const save = React.useCallback(() => {
    onUpdate(_.sortBy(Object.keys(currentState)));
  }, [onUpdate, currentState]);

  const RowItem = React.useCallback(
    ({ index, style }: any) => {
      const { value } = filteredChoices[index];
      let { label } = filteredChoices[index];
      label = label.slice(0, 50);
      return (
        <div style={style}>
          <Checkbox
            label={label}
            value={currentState[value]}
            onChange={() => toggle(value)}
          />
        </div>
      );
    },
    [filteredChoices, toggle, currentState],
  );

  return (
    <>
      <p>
        <strong>Select values for label key:</strong> {labelKey}
      </p>
      <hr />
      <TextInput
        className="mb-2"
        placeholder="Search..."
        value={filterText}
        onChange={(event) => setFilterText(event.target.value)}
      />
      <div className="flex items-center mb-1">
        <div className="flex-grow text-muted">
          <small>{numSelected} selected</small>
        </div>
        <div>
          <TertiaryButton onClick={selectAll}>Select all</TertiaryButton>&nbsp;
          <TertiaryButton onClick={clear}>Clear</TertiaryButton>
        </div>
      </div>
      <AutoSizer disableHeight>
        {({ width }) => (
          <List
            height={240}
            itemCount={filteredChoices.length}
            itemSize={30}
            width={width}
          >
            {RowItem}
          </List>
        )}
      </AutoSizer>
      <hr />
      <PrimaryButton onClick={save} disabled={!canSave || disabled}>
        Save
      </PrimaryButton>
      <TertiaryButton className="ml-2" onClick={closeDialog}>
        Close
      </TertiaryButton>
    </>
  );
}

interface BreakdownListProps {
  breakdowns: Breakdown[];
  breakdownDisabled: boolean;
  allConfigs: BreakdownConfig[];
  onBreakdownUpdate: (key: string, update: BreakdownUpdate | null) => void;
  includeOtherCheckbox: boolean;
}

const BreakdownList = SortableContainer<BreakdownListProps>(
  (props: BreakdownListProps) => {
    const {
      breakdowns,
      breakdownDisabled,
      allConfigs,
      onBreakdownUpdate,
      includeOtherCheckbox,
    } = props;
    return (
      <div>
        {breakdowns.map((breakdown: Breakdown, index: number) => (
          <BreakdownBox
            index={index}
            key={breakdown.key}
            breakdownIndex={index}
            current={pick(breakdown, "values", "groupUnselectedIntoOther")}
            breakdownDisabled={breakdownDisabled}
            disabled={breakdownDisabled}
            config={pickConfig(breakdown.key, allConfigs)}
            onUpdate={onBreakdownUpdate}
            includeOtherCheckbox={includeOtherCheckbox}
          />
        ))}
      </div>
    );
  },
);

function newBreakdownFromConfig(config: BreakdownConfig): Breakdown {
  const { key } = config;
  if (config.type === "categorical") {
    return {
      key,
      values: [...config.choices.map(({ value }: any) => value)],
    };
  } else {
    return {
      key,
      values: [],
    };
  }
}

export function pickConfig(
  key: string,
  configs: BreakdownConfig[],
): BreakdownConfig {
  return _.chain(configs)
    .filter((v) => v.key === key)
    .first()
    .value();
}

export function ExpandableBreakdownSelector({
  label = "BREAKDOWN BY",
  onUpdate,
  current,
  buttonClassName,
  editBreakdownButton = true,
  ...rest
}: BreakdownSelectorProps) {
  const { user } = useUser();
  const [isExpanded, setExpanded] = React.useState(false);
  const [proposed, setProposed] = React.useState(current);

  useEffect(() => {
    setProposed(current);
  }, [current]);

  const applyBreakdowns = React.useCallback(() => {
    onUpdate(proposed);
    setExpanded(false);
  }, [onUpdate, proposed, setExpanded]);

  return (
    <>
      <Popover className="nb-input-container relative">
        {label && <label className="label">{label}</label>}
        <PopoverButton
          className={clsx(
            buttonClassName
              ? buttonClassName
              : "nb-input flex items-center max-w-xs font-normal",
          )}
          // className="nb-input flex items-center max-w-xs font-normal"
          onClick={() => setExpanded(true)}
        >
          {({ open }) => (
            <div className="flex items-center space-between w-full">
              <div
                className={clsx(
                  "text-left text-ellipsis overflow-hidden",
                  "whitespace-nowrap flex-grow text-sm",
                )}
              >
                {current.map((v) => v.key).join(", ") || (
                  <span className="text-muted">(none)</span>
                )}
              </div>
              {renderChevronAnimation(open)}
            </div>
          )}
        </PopoverButton>
        <Transition
          as={Fragment}
          show={isExpanded}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            static
            className="absolute left-0 z-100 mt-2 elevation-1 rounded-md p-3 bg-white w-80"
          >
            <BreakdownSelector
              {...rest}
              current={proposed}
              onUpdate={(v) => setProposed(v)}
            />
            <hr />
            <div className="flex justify-between items-center">
              <PrimaryButton onClick={applyBreakdowns}>APPLY</PrimaryButton>
              {editBreakdownButton && (
                <Link to={user.pathFromRoot("/breakdown-editor")}>
                  <SecondaryButton>EDIT BREAKDOWNS</SecondaryButton>
                </Link>
              )}
            </div>
          </PopoverPanel>
        </Transition>
      </Popover>
      {isExpanded && <Blanket onClick={() => setExpanded(false)} />}
    </>
  );
}
